import config from "appConfig";

const theory = ` <h3 class=sectionHead>Introduction</h3>
<p>A single loudspeaker cannot generate a high sound level with high fidelity over the entire audio band, <em><span class=cmti-10>i.e.</span></em> between 20 Hz and 20 kHz. In addition, the directivity of a single loudspeaker changes too much over a wide frequency band. It is therefore necessary to associate several drivers in a cabinet to obtain a wide frequency range and a directivity that remains as constant as possible. The simplest combination is to use a woofer dedicated to the low and medium frequencies and a tweeter for the high frequencies. It is then necessary to split the audio signal into two frequency bands, one for each speaker, using a crossover filter [1, 3, 4]. This ensures that each loudspeaker operates only in the frequency band it was designed for and that the frequency response of the system, obtained by summing the radiated pressures from the two drivers, has the desired shape. This Lesson presents the different solutions used by manufacturing companies.
<h3 class=sectionHead>Overview of crossover types</h3>
<p>As mentioned above, crossovers are used in loudspeaker systems to split the audio signal into different frequency bands dedicated to each speaker. The most commonly used systems are:
<ul class=itemize1>
   <li class=itemize>two-way systems where the audio signal is separated into two bands: one for the woofer and one for the tweeter,
   <li class=itemize>three-way systems, where the three output signals are assigned to drive the woofer, midrange and tweeter.
</ul>
<p>The crossover filters have different purposes:
<ul class=itemize1>
   <li class=itemize>they allow to send to each driver only the frequencies corresponding to its bandwidth, thus reducing the risks of breakage and the generation of distortion,
   <li class=itemize>to obtain the desired frequency response after the pressures radiated by each speaker are summed,
   <li class=itemize>correct the response of a driver, for instance according to the input level for active filters in bass enhancement applications.
</ul>
<p>Crossover filters are classified into two families:
<ul class=itemize1>
   <li class=itemize>passive filters, generally placed after the power amplifier. These crossovers have the advantage of requiring only one amplifier per speaker system, but require components that must handle large power levels - a few dozen to a few thousand Watts depending on the application,
   <li class=itemize>active crossovers are placed before the power amplifier stage and require one amplifier per output band and are therefore operating at levels that are consistent with amplifier inputs.
</ul>
<p>
   Schematic diagrams of a 2-way enclosure with passive or active filters are given in <a href=# data-img-alt="Figure 1. Loudspeaker system with passive or active filters. LP stands for low-pass, HP for high-pass and BP for band-pass." data-img-src=${config.ASSETS_URL}/04/23/theory/figure-1.svg>Figure 1</a>.
   <figure class=figure>
<p><img alt="Figure 1. Loudspeaker system with passive or active filters. LP stands for low-pass, HP for high-pass and BP for band-pass." src=${config.ASSETS_URL}/04/23/theory/figure-1.svg><figcaption class=caption>Figure 1. Loudspeaker system with passive or active filters. LP stands for low-pass, HP for high-pass and BP for band-pass.</figcaption></figure>
<h3 class=sectionHead>Passive filters</h3>
<p>
   Passive filters are made of passive components such as capacitors, inductors and resistors which are usually arranged using ladder topologies. Parallel crossovers networks are generally preferred as they are easier to design, classical examples are given in <a href=# data-img-alt="Figure 2. First order low and high-pass filters." data-img-src=${config.ASSETS_URL}/04/23/theory/figure-2.svg>Figure 2</a> for first order filters and in <a href=# data-img-alt="Figure 3. Second order low and high-pass filters." data-img-src=${config.ASSETS_URL}/04/23/theory/figure-3.svg>Figure 3</a> for second order filters. First order filters have a
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>±</mo>
      <mn>6</mn>
   </math>
   dB slope per octave while second order filters exhibit a
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>±</mo>
      <mn>12</mn>
   </math>
   dB slope per octave. First-order filters are known to preserve amplitude and phase in the relevant bandwidth. However, due to their lower slope, the tweeter may not be sufficiently protected against large amplitude low frequency signals. Resistors can also be added to match radiated pressure levels when drivers have different efficiencies.
   <figure class=figure>
<p><img alt="Figure 2. First order low and high-pass filters." src=${config.ASSETS_URL}/04/23/theory/figure-2.svg><figcaption class=caption>Figure 2. First order low and high-pass filters.</figcaption></figure><figure class=figure><p><img alt="Figure 3. Second order low and high-pass filters." src=${config.ASSETS_URL}/04/23/theory/figure-3.svg><figcaption class=caption>Figure 3. Second order low and high-pass filters.</figcaption></figure>
<p>
   The frequency responses for a constant resistance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   load is given in <a href=# data-img-alt="Figure 4. Frequency responses of first order filters." data-img-src=${config.ASSETS_URL}/04/23/theory/figure-4.svg>Figure 4</a> for the first order filters of <a href=# data-img-alt="Figure 2. First order low and high-pass filters." data-img-src=${config.ASSETS_URL}/04/23/theory/figure-2.svg>Figure 2</a>. The crossover frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   is linked to the filter components with the following relations:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>C</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>∕</mo>
      <mo class=MathClass-open>(</mo>
      <mn>2</mn>
      <mi>π</mi>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mi>R</mi>
      <mo class=MathClass-close>)</mo>
   </math> and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>L</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>R</mi>
      <mo class=MathClass-bin>∕</mo>
      <mo class=MathClass-open>(</mo>
      <mn>2</mn>
      <mi>π</mi>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
   </math>. It can be seen that the sum of the responses of the low and high-pass filters gives a constant response.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 4. Frequency responses of first order filters." src=${config.ASSETS_URL}/04/23/theory/figure-4.svg><figcaption class=caption>Figure 4. Frequency responses of first order filters.</figcaption></figure>
<p>
   For second order filters, due to the filter phase shift, the frequency responses cancel each other at the crossover frequency. A common solution is to invert the tweeter polarity as shown in <a href=# data-img-alt="Figure 3. Second order low and high-pass filters." data-img-src=${config.ASSETS_URL}/04/23/theory/figure-3.svg>Figure 3</a> to get the responses plotted in <a href=# data-img-alt="Figure 5. Frequency responses of second order filters. Solid lines are for Butterworth filters while dashed lines represents Linkwitz Riley filter responses." data-img-src=${config.ASSETS_URL}/04/23/theory/figure-5.svg>Figure 5</a>. Butterworth filters are obtained for:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>C</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>∕</mo>
      <mo class=MathClass-open>(</mo>
      <mn>2</mn>
      <msqrt>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msqrt>
      <mi>π</mi>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mi>R</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>L</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>R</mi>
      <mo class=MathClass-bin>∕</mo>
      <mo class=MathClass-open>(</mo>
      <mn>2</mn>
      <msqrt>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msqrt>
      <mi>π</mi>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
   </math>. Note that the sum for Butterworth filter responses (solid lines) has a 3 dB bump at the cut-off frequency. An alternative solution is to use the so-called Linkwitz Riley filters (dashed lines)  which are conceived with a 6 dB drop at the crossover frequency in order to obtain a constant sum. In this case, we have
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>C</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>∕</mo>
      <mo class=MathClass-open>(</mo>
      <mn>4</mn>
      <mi>π</mi>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mi>R</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>L</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>R</mi>
      <mo class=MathClass-bin>∕</mo>
      <mo class=MathClass-open>(</mo>
      <msqrt>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msqrt>
      <mi>π</mi>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
   </math>.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 5. Frequency responses of second order filters. Solid lines are for Butterworth filters while dashed lines represents Linkwitz Riley filter responses." src=${config.ASSETS_URL}/04/23/theory/figure-5.svg><figcaption class=caption>Figure 5. Frequency responses of second order filters. Solid lines are for Butterworth filters while dashed lines represents Linkwitz Riley filter responses.</figcaption></figure>
<p>It should be recalled that these filter responses have been provided for a real and constant speaker impedance, which is not the case in reality. Indeed, the resonance due to the moving coil at low frequencies and the inductance of the coil at high frequencies can strongly modify the filter responses. The design of the filter must therefore be carried out taking into account a model or a measurement of the impedances of the loudspeakers considered.<br class=newline>Note that it is also possible to use third-order filters, which are still fairly simple to design and which provide a flat amplitude summation as for first-order filters. For filter orders larger than 4, their implementation in active version is often simpler and should be preferred. Modern filters can also equalise the electrical impedance of the loudspeaker. For example, Zobel networks can be used to ensure that the impedance a loudspeaker has at the output of its amplifier appears as a pure resistance.<br class=newline>Although passive crossover components are more expensive because they have to withstand high power levels, they are an interesting solution because they are easy to design for small orders and allow only one mono amplifier to be used per speaker. However, these have the following drawbacks:
<ul class=itemize1>
   <li class=itemize>the crossovers can be quite voluminous and may not be suitable for small speakers.
   <li class=itemize>as the driver impedance can change with the input voltage (increase of the resistance with heating, dependence of compliance and inductance on input level), the filter response will change as it also depends on its electrical load.
   <li class=itemize>resistive elements (filter resistors where used and resistive losses in inductor windings) cause power losses.
</ul>
<p>To overcome these drawbacks active filters may be used.
<h3 class=sectionHead>Active filters</h3>
<p>Active filters are electronic circuits that use at least one active component. Today they largely use operational amplifiers, but are increasingly implemented digitally on a Digital Signal Processor (DSP) or other microprocessors. As they work at line level, they are placed upstream of a power amplifier. Active crossovers always require the use of amplifiers for each sub-band. Thus, an active crossover for a 2-way system requires one amplifier for the woofer and one for the tweeter. Despite this extra cost, active crossovers have the following advantages:
<ul class=itemize1>
   <li class=itemize>their response is independent of speaker impedance variations,
   <li class=itemize>they are more scalable and easier to tune,
   <li class=itemize>they provide less coupling between the drivers,
   <li class=itemize>in contrast to passive filters, there are no power losses downstream of the amplifier in components other than the loudspeaker, as the it is directly connected to the amplifier,
   <li class=itemize>they are easy to miniaturize and are now widely used with class D amplifiers for portable speakers.
   <li class=itemize>they allow for implementation of other features such as equalisation to obtain a flat response of the drivers, room equalisation, phase alignment with respect to other drivers/units, limiters, implementation of features associated to psychoacoustics (for example, fundamental loss), noise/echo cancellation, beamforming in combination with more sources, etc.
   <li class=itemize>they offer a high degree of reproducibility because the tolerance values associated with analog components are avoided,
   <li class=itemize>they are reprogrammable, so enhanced functions can be incorporated without hardware modification.
</ul>
<p>Whatever the type of filter (analog or digital), destructive or constructive interference is created around the crossover frequency between the pressure fields radiated by the filtered loudspeakers. This creates directivity lobes, the characteristics of which depend on the relative amplitudes and phases of the loudspeakers and their spacing. One solution to minimise this effect is to use coaxial loudspeakers or to place the tweeter between two midwoofers in order to direct the main lobe towards the listener [2].
<h3 class=sectionHead>Conclusion</h3>
<p>In this Lesson, different technical solutions used for crossover filters were presented. They allow the distribution of the audio band between the different drivers of a loudspeaker system. The goal is to achieve the target frequency response while keeping the directivity approximately constant by using drivers of different diameters. Remember that crossovers should not introduce audible artefacts, <em><span class=cmti-10>e.g.</span></em> if the quality factors of the crossovers are too high, or if too large phase shifts lengthen the impulse response.
<h3 class=likesectionHead>Glossary</h3>
<dl>
  <dt>DSP
  <dd>Digital Signal Processor
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-ashley1961transient class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p>J Robert Ashley. “On the transient response of ideal crossover networks”. In: <span class=cmti-10>Audio Engineering Society Convention 13</span>. Audio Engineering Society. 1961.
   <dt id=X0-linkwitz1976active class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p>Joseph A. D’Appolito. “A Geometric Approach to Eliminating Lobing Error in Multiway Loudspeakers”.  In: <span class=cmti-10>Audio Engineering Society Convention 74. </span>Audio Engineering Society. Oct. 1983. 
   <dt id=X0-linkwitz1976active class=thebibliography>[3]
   <dd id=bib-2 class=thebibliography>
      <p>Siegfried H Linkwitz. “Active crossover networks for noncoincident drivers”. In: <span class=cmti-10>Journal of the Audio Engineering Society </span>24.1 (1976), pp. 2–8.
   <dt id=X0-small1971constant class=thebibliography>[4]
   <dd id=bib-3 class=thebibliography>
      <p>Richard H Small. “Constant-voltage crossover network design”. In: <span class=cmti-10>Journal </span><span class=cmti-10>of the Audio Engineering Society </span>19.1 (1971), pp. 12–19.
</dl>`;

export default theory;
